import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  CalendarDays,
  ChevronLeft,
  ChevronRight,
  Plus,
  Trash2,
  MapPin,
  X,
} from "lucide-react";
import client from "@/lib/api";

const MONTHS_IT = [
  "Gennaio", "Febbraio", "Marzo", "Aprile", "Maggio", "Giugno",
  "Luglio", "Agosto", "Settembre", "Ottobre", "Novembre", "Dicembre",
];

const DAYS_IT = ["Lu", "Ma", "Me", "Gi", "Ve", "Sa", "Do"];

function daysInMonth(year, month) {
  return new Date(year, month + 1, 0).getDate();
}

function firstWeekday(year, month) {
  const d = new Date(year, month, 1).getDay();
  return d === 0 ? 6 : d - 1;
}

function dateStr(y, m, d) {
  return `${y}-${String(m + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
}

function formatDisplayDate(isoDate) {
  const [y, m, d] = isoDate.split("-");
  return `${d}/${m}/${y}`;
}

function toIsoDate(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

function buildDateRange(startIso, endIso) {
  const dates = [];
  const start = new Date(`${startIso}T00:00:00`);
  const end = new Date(`${endIso}T00:00:00`);

  for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
    dates.push(toIsoDate(d));
  }

  return dates;
}

export default function EventsModal({ isAdmin }) {
  const [open, setOpen] = useState(false);
  const [view, setView] = useState("calendar");
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const today = new Date();
  const [viewYear, setViewYear] = useState(today.getFullYear());
  const [viewMonth, setViewMonth] = useState(today.getMonth());
  const [selectedDate, setSelectedDate] = useState(null);

  const [formTitle, setFormTitle] = useState("");
  const [formLocation, setFormLocation] = useState("");
  const [formNotes, setFormNotes] = useState("");
  const [formStartDate, setFormStartDate] = useState(toIsoDate(today));
  const [formEndDate, setFormEndDate] = useState(toIsoDate(today));

  const fetchEvents = async () => {
    setLoading(true);
    try {
      const res = await client.get("/events");
      setEvents(res.data);
    } catch {
      setError("Impossibile caricare gli eventi.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) fetchEvents();
  }, [open]);

  const eventsByDate = {};
  events.forEach((ev) => {
    ev.dates.forEach((d) => {
      if (!eventsByDate[d]) eventsByDate[d] = [];
      eventsByDate[d].push(ev);
    });
  });

  const selectedEvents = selectedDate ? eventsByDate[selectedDate] || [] : [];

  const prevMonth = () => {
    setSelectedDate(null);
    if (viewMonth === 0) {
      setViewMonth(11);
      setViewYear((y) => y - 1);
    } else {
      setViewMonth((m) => m - 1);
    }
  };

  const nextMonth = () => {
    setSelectedDate(null);
    if (viewMonth === 11) {
      setViewMonth(0);
      setViewYear((y) => y + 1);
    } else {
      setViewMonth((m) => m + 1);
    }
  };

  const resetForm = () => {
    setFormTitle("");
    setFormLocation("");
    setFormNotes("");
    setFormStartDate(toIsoDate(new Date()));
    setFormEndDate(toIsoDate(new Date()));
    setError("");
  };

  const saveEvent = async () => {
    if (!formTitle.trim()) {
      setError("Il titolo e obbligatorio.");
      return;
    }

    if (!formStartDate || !formEndDate) {
      setError("Seleziona data inizio e data fine.");
      return;
    }

    if (formEndDate < formStartDate) {
      setError("La data fine non puo essere precedente alla data inizio.");
      return;
    }

    const dates = buildDateRange(formStartDate, formEndDate);

    setSaving(true);
    setError("");

    try {
      await client.post("/events", {
        title: formTitle.trim(),
        location: formLocation.trim(),
        notes: formNotes.trim(),
        dates,
      });

      resetForm();
      setView("calendar");
      await fetchEvents();
    } catch (e) {
      setError(e?.response?.data?.detail || "Errore nel salvataggio.");
    } finally {
      setSaving(false);
    }
  };

  const deleteEvent = async (id) => {
    if (!window.confirm("Eliminare questo evento?")) return;

    try {
      await client.delete(`/events/${id}`);
      setSelectedDate(null);
      await fetchEvents();
    } catch {
      setError("Errore nell'eliminazione.");
    }
  };

  const totalDays = daysInMonth(viewYear, viewMonth);
  const offset = firstWeekday(viewYear, viewMonth);
  const cells = [];

  for (let i = 0; i < offset; i++) cells.push(null);
  for (let d = 1; d <= totalDays; d++) cells.push(d);

  const todayStr = dateStr(today.getFullYear(), today.getMonth(), today.getDate());

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        title="Calendario eventi"
        className="inline-flex items-center gap-1.5 text-sm text-[#2B593F] dark:text-[#8FCBA7] hover:bg-[#F5F1EA] dark:hover:bg-[#2B593F]/15 px-2.5 sm:px-3 py-2 rounded-xl font-medium transition-colors"
      >
        <CalendarDays size={16} />
        <span className="hidden sm:inline">Eventi</span>
      </button>

      <Dialog
        open={open}
        onOpenChange={(o) => {
          // Se stiamo chiudendo la modale ma siamo nella vista form, 
          // torniamo semplicemente al calendario invece di chiudere tutto.
          if (!o && view === "form") {
            setView("calendar");
            resetForm();
            return;
          }
          setOpen(o);
          if (!o) {
            setView("calendar");
            resetForm();
            setSelectedDate(null);
          }
        }}
      >
        <DialogContent className={`max-w-lg w-full bg-[#FDFBF7] dark:bg-[#1A1A1A] border border-[#EAE3D9] dark:border-[#2A2A2A] rounded-2xl p-0 overflow-visible ${view === "form" ? "[&>button]:hidden" : ""}`}>
          {view === "calendar" && (
            <div className="relative">
              <DialogHeader className="px-6 pt-6 pb-2">
                <DialogTitle className="text-[#2D2A26] dark:text-[#F5F1EA] font-display text-lg flex items-center gap-2">
                  <CalendarDays size={18} className="text-[#2B593F]" />
                  Calendario eventi
                </DialogTitle>
              </DialogHeader>

              <div className="flex items-center justify-between px-6 py-3">
                <button onClick={prevMonth} className="p-1.5 rounded-lg hover:bg-[#EAE3D9] dark:hover:bg-[#2A2A2A] transition-colors">
                  <ChevronLeft size={18} className="text-[#757169] dark:text-[#B8B2AA]" />
                </button>

                <span className="font-semibold text-[#2D2A26] dark:text-[#F5F1EA]">
                  {MONTHS_IT[viewMonth]} {viewYear}
                </span>

                <button onClick={nextMonth} className="p-1.5 rounded-lg hover:bg-[#EAE3D9] dark:hover:bg-[#2A2A2A] transition-colors">
                  <ChevronRight size={18} className="text-[#757169] dark:text-[#B8B2AA]" />
                </button>
              </div>

              <div className="grid grid-cols-7 px-4">
                {DAYS_IT.map((d) => (
                  <div key={d} className="text-center text-xs font-medium text-[#A09890] dark:text-[#6B6560] py-1">
                    {d}
                  </div>
                ))}
              </div>

              <div className="grid grid-cols-7 px-4 pb-4">
                {cells.map((day, i) => {
                  if (!day) return <div key={`e-${i}`} />;

                  const ds = dateStr(viewYear, viewMonth, day);
                  const evs = eventsByDate[ds] || [];
                  const isToday = ds === todayStr;
                  const isSelected = ds === selectedDate;

                  return (
                    <div
                      key={ds}
                      onClick={() => evs.length > 0 ? setSelectedDate(ds) : setSelectedDate(null)}
                      className={`aspect-square flex flex-col items-center justify-start pt-1 relative rounded-xl transition-colors ${
                        evs.length > 0
                          ? "cursor-pointer hover:bg-[#F5F1EA] dark:hover:bg-[#242424]"
                          : "cursor-default"
                      }`}
                    >
                      <span
                        className={`text-sm w-7 h-7 flex items-center justify-center rounded-full font-medium transition-colors ${
                          isSelected
                            ? "bg-[#C8553D] text-white"
                            : isToday
                            ? "bg-[#2B593F] text-white"
                            : "text-[#2D2A26] dark:text-[#F5F1EA]"
                        }`}
                      >
                        {day}
                      </span>

                      {evs.length > 0 && (
                        <div className="flex gap-0.5 mt-0.5 flex-wrap justify-center">
                          {evs.slice(0, 2).map((ev, idx) => (
                            <span key={idx} className="w-1.5 h-1.5 rounded-full bg-[#C8553D]" title={ev.title} />
                          ))}
                          {evs.length > 2 && <span className="w-1.5 h-1.5 rounded-full bg-[#A09890]" />}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              {selectedDate && selectedEvents.length > 0 && (
                <div className="absolute left-4 right-4 bottom-20 z-50 rounded-2xl border border-[#EAE3D9] dark:border-[#2A2A2A] bg-[#FDFBF7] dark:bg-[#1A1A1A] shadow-2xl p-3">
                  <div className="flex items-center justify-between gap-2 mb-2">
                    <p className="text-xs font-semibold text-[#757169] dark:text-[#B8B2AA]">
                      Eventi del {formatDisplayDate(selectedDate)}
                    </p>
                    <button
                      onClick={() => setSelectedDate(null)}
                      className="p-1 rounded-lg hover:bg-[#EAE3D9] dark:hover:bg-[#2A2A2A] text-[#757169]"
                    >
                      <X size={14} />
                    </button>
                  </div>

                  <div className="space-y-2 max-h-48 overflow-y-auto pr-1">
                    {selectedEvents.map((ev) => (
                      <div
                        key={ev.id}
                        className="flex items-start justify-between gap-2 bg-[#F5F1EA] dark:bg-[#242424] rounded-xl px-3 py-2.5"
                      >
                        <div className="min-w-0">
                          <p className="font-semibold text-sm text-[#2D2A26] dark:text-[#F5F1EA] truncate">
                            {ev.title}
                          </p>

                          {ev.location && (
                            <p className="text-xs text-[#757169] dark:text-[#B8B2AA] flex items-center gap-0.5 mt-0.5">
                              <MapPin size={10} />
                              {ev.location}
                            </p>
                          )}

                          {ev.notes && (
                            <p className="text-xs text-[#A09890] dark:text-[#6B6560] mt-0.5">
                              {ev.notes}
                            </p>
                          )}
                        </div>

                        {isAdmin && (
                          <button
                            onClick={() => deleteEvent(ev.id)}
                            className="shrink-0 p-1 rounded-lg hover:bg-[#F4DAD3]/60 dark:hover:bg-[#C8553D]/20 text-[#C8553D] transition-colors"
                          >
                            <Trash2 size={14} />
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {loading && (
                <p className="text-sm text-center text-[#A09890] pb-3">
                  Caricamento…
                </p>
              )}

              {error && <p className="text-xs text-[#C8553D] pb-3 text-center">{error}</p>}

              {isAdmin && (
                <div className="px-4 pb-5 flex justify-end">
                  <Button
                    onClick={() => {
                      resetForm();
                      setSelectedDate(null);
                      setView("form");
                    }}
                    className="bg-[#2B593F] hover:bg-[#234a34] text-white rounded-xl gap-1.5"
                  >
                    <Plus size={15} />
                    Inserisci evento
                  </Button>
                </div>
              )}
            </div>
          )}

          {view === "form" && (
            <div>
              <DialogHeader className="px-6 pt-6 pb-2">
                <div className="flex items-center justify-between">
                  <DialogTitle className="text-[#2D2A26] dark:text-[#F5F1EA] font-display text-lg">
                    Nuovo evento
                  </DialogTitle>

                  <button
                    onClick={() => {
                      setView("calendar");
                      resetForm();
                    }}
                    className="p-1.5 rounded-lg hover:bg-[#EAE3D9] dark:hover:bg-[#2A2A2A] text-[#757169]"
                  >
                    <X size={16} />
                  </button>
                </div>
              </DialogHeader>

              <div className="px-6 pb-6 space-y-4 mt-2">
                <div>
                  <label className="text-xs font-medium text-[#757169] dark:text-[#B8B2AA] mb-1 block">
                    Titolo *
                  </label>
                  <Input
                    placeholder="es. Bologna Play 2025"
                    value={formTitle}
                    onChange={(e) => setFormTitle(e.target.value)}
                    className="bg-white dark:bg-[#242424] border-[#EAE3D9] dark:border-[#2A2A2A] text-[#2D2A26] dark:text-[#F5F1EA] rounded-xl"
                  />
                </div>

                <div>
                  <label className="text-xs font-medium text-[#757169] dark:text-[#B8B2AA] mb-1 block">
                    Luogo
                  </label>
                  <Input
                    placeholder="es. Bologna, Fiera"
                    value={formLocation}
                    onChange={(e) => setFormLocation(e.target.value)}
                    className="bg-white dark:bg-[#242424] border-[#EAE3D9] dark:border-[#2A2A2A] text-[#2D2A26] dark:text-[#F5F1EA] rounded-xl"
                  />
                </div>

                <div>
                  <label className="text-xs font-medium text-[#757169] dark:text-[#B8B2AA] mb-1 block">
                    Date *
                  </label>

                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <span className="text-[11px] text-[#A09890] dark:text-[#6B6560]">
                        Da
                      </span>
                      <Input
                        type="date"
                        value={formStartDate}
                        onChange={(e) => {
                          const value = e.target.value;
                          setFormStartDate(value);
                          if (formEndDate < value) {
                            setFormEndDate(value);
                          }
                        }}
                        className="mt-1 bg-white dark:bg-[#242424] border-[#EAE3D9] dark:border-[#2A2A2A] text-[#2D2A26] dark:text-[#F5F1EA] dark:[color-scheme:dark] rounded-xl"
                      />
                    </div>

                    <div>
                      <span className="text-[11px] text-[#A09890] dark:text-[#6B6560]">
                        A
                      </span>
                      <Input
                        type="date"
                        value={formEndDate}
                        min={formStartDate}
                        onChange={(e) => setFormEndDate(e.target.value)}
                        className="mt-1 bg-white dark:bg-[#242424] border-[#EAE3D9] dark:border-[#2A2A2A] text-[#2D2A26] dark:text-[#F5F1EA] dark:[color-scheme:dark] rounded-xl"
                      />
                    </div>
                  </div>

                  <p className="text-xs text-[#A09890] dark:text-[#6B6560] mt-2">
                    Periodo selezionato:{" "}
                    <span className="text-[#C8553D] font-medium">
                      {formatDisplayDate(formStartDate)}
                      {formEndDate !== formStartDate ? ` → ${formatDisplayDate(formEndDate)}` : ""}
                    </span>
                  </p>
                </div>

                <div>
                  <label className="text-xs font-medium text-[#757169] dark:text-[#B8B2AA] mb-1 block">
                    Note
                  </label>
                  <Textarea
                    placeholder="Descrizione, link, info utili…"
                    value={formNotes}
                    onChange={(e) => setFormNotes(e.target.value)}
                    rows={3}
                    className="bg-white dark:bg-[#242424] border-[#EAE3D9] dark:border-[#2A2A2A] text-[#2D2A26] dark:text-[#F5F1EA] placeholder:text-[#A09890] dark:placeholder:text-[#6B6560] rounded-xl resize-none"
                  />
                </div>

                {error && <p className="text-xs text-[#C8553D]">{error}</p>}

                <div className="flex gap-2 justify-end pt-1">
                  <Button
                    variant="outline"
                    onClick={() => {
                      setView("calendar");
                      resetForm();
                    }}
                    className="rounded-xl border-[#EAE3D9] dark:border-[#2A2A2A]"
                  >
                    Annulla
                  </Button>

                  <Button
                    onClick={saveEvent}
                    disabled={saving}
                    className="bg-[#2B593F] hover:bg-[#234a34] text-white rounded-xl"
                  >
                    {saving ? "Salvataggio…" : "Salva evento"}
                  </Button>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
